'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, ArrowRight, Copy, User, Building2, Mail, Lock, Phone } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';


type AuthMode = 'login' | 'signup';
type UserRole = 'player' | 'manager';

interface LoginForm {
  email: string;
  password: string;
  remember: boolean;
}

interface SignupForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  confirmPassword: string;
  hotelName?: string;
  hotelCity?: string;
  terms: boolean;
}

const demoCredentials = [
  { role: 'Player', email: 'sophie.martin@gmail.com', password: 'PadelTN2026!' },
  { role: 'Hotel Manager', email: 'karim.amiri@seabelhotels.com', password: 'Manager@Seabel26' },
];

export default function AuthPanel() {
  const router = useRouter();
  const [mode, setMode] = useState<AuthMode>('login');
  const [role, setRole] = useState<UserRole>('player');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);

  const loginForm = useForm<LoginForm>({ defaultValues: { email: '', password: '', remember: false } });
  const signupForm = useForm<SignupForm>({ defaultValues: { firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '', hotelName: '', hotelCity: '', terms: false } });

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
    toast.success(`${field} copied`);
  };

  const autofillCredentials = (cred: typeof demoCredentials[0]) => {
    loginForm.setValue('email', cred.email);
    loginForm.setValue('password', cred.password);
    setRole(cred.role === 'Hotel Manager' ? 'manager' : 'player');
    toast.info(`Demo credentials filled for ${cred.role}`);
  };

  // Backend integration point: POST /api/auth/login
  const handleLogin = async (data: LoginForm) => {
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1500));
    setLoading(false);

    const validCreds = demoCredentials.find(
      (c) => c.email === data.email && c.password === data.password
    );
    if (!validCreds) {
      loginForm.setError('email', {
        message: 'Invalid credentials — use the demo accounts below to sign in',
      });
      return;
    }

    toast.success(`Welcome back! Redirecting to your ${validCreds.role === 'Hotel Manager' ? 'dashboard' : 'courts'}.`);
    setTimeout(() => {
      router.push(validCreds.role === 'Hotel Manager' ? '/hotel-manager-dashboard' : '/court-discovery');
    }, 800);
  };

  // Backend integration point: POST /api/auth/register
  const handleSignup = async (data: SignupForm) => {
    if (data.password !== data.confirmPassword) {
      signupForm.setError('confirmPassword', { message: 'Passwords do not match' });
      return;
    }
    setLoading(true);
    await new Promise((r) => setTimeout(r, 1800));
    setLoading(false);
    toast.success('Account created! Check your email to verify your address.');
    setMode('login');
  };

  return (
    <div className="flex w-full min-h-screen">
      {/* Left Panel — Brand / Visual */}
      <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 relative overflow-hidden bg-gradient-to-br from-[#FF2D1F] via-[#cc1a0e] to-[#8a0f08] flex-col justify-between p-12">
        {/* Decorative circles */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-20 -translate-x-20" />
        <div className="absolute top-1/2 left-1/2 w-[600px] h-[600px] bg-white/3 rounded-full -translate-x-1/2 -translate-y-1/2" />

        {/* Logo */}
        <div className="relative flex items-center gap-3">
          <AppLogo size={44} />
          <span className="text-2xl font-bold text-white tracking-tight">
            Tunisia<span className="text-secondary">Padel</span>
          </span>
        </div>

        {/* Center Content */}
        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/15 border border-white/25 mb-6">
            <span className="w-2 h-2 rounded-full bg-secondary animate-pulse-subtle" />
            <span className="text-sm font-medium text-white/90">Tunisia's #1 Padel Booking Platform</span>
          </div>
          <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight mb-4">
            Your Next Padel
            <br />
            <span className="text-secondary">Adventure</span> Awaits
          </h1>
          <p className="text-lg text-white/70 leading-relaxed mb-8 max-w-md">
            Book courts at Tunisia's finest hotels. From Hammamet to Sousse — reserve in minutes, play the same day.
          </p>

          {/* Feature list */}
          <div className="space-y-3">
            {[
              'Real-time court availability',
              'Stay & Play hotel bundles',
              'Secure payments in EUR & TND',
              'Instant booking confirmation',
            ].map((feat) => (
              <div key={`feat-${feat}`} className="flex items-center gap-3">
                <CheckCircle size={17} className="text-secondary flex-shrink-0" />
                <span className="text-sm text-white/80">{feat}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom Testimonial */}
        <div className="relative bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 p-5">
          <p className="text-sm text-white/85 italic leading-relaxed mb-3">
            "Booked a court at Seabel Alhambra in under 2 minutes. The Stay & Play bundle saved us so much hassle — highly recommended for any padel trip to Tunisia!"
          </p>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-xs font-bold text-white">TD</span>
            </div>
            <div>
              <p className="text-sm font-semibold text-white">Thomas Dupuis</p>
              <p className="text-xs text-white/60">🇫🇷 Paris, France · Padel player since 2021</p>
            </div>
          </div>
        </div>
      </div>

      {/* Right Panel — Auth Form */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-12 xl:px-16 py-12 bg-background overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          {/* Mobile Logo */}
          <div className="flex lg:hidden items-center gap-2.5 mb-8">
            <AppLogo size={36} />
            <span className="text-xl font-bold text-foreground">
              Tunisia<span className="text-primary">Padel</span>
            </span>
          </div>

          {/* Mode Toggle */}
          <div className="flex rounded-xl border border-border bg-muted p-1 mb-8">
            {(['login', 'signup'] as AuthMode[]).map((m) => (
              <button
                key={`mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 py-2.5 rounded-lg text-sm font-semibold capitalize transition-all duration-200 ${
                  mode === m ? 'bg-white text-foreground shadow-card' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {m === 'login' ? 'Sign In' : 'Create Account'}
              </button>
            ))}
          </div>

          {/* Role Toggle */}
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">I am a...</p>
            <div className="grid grid-cols-2 gap-2">
              {([
                { key: 'player', label: 'Player / Tourist', icon: User, desc: 'Book courts & play' },
                { key: 'manager', label: 'Hotel Manager', icon: Building2, desc: 'Manage my facility' },
              ] as const).map((r) => {
                const Icon = r.icon;
                return (
                  <button
                    key={`role-${r.key}`}
                    onClick={() => setRole(r.key)}
                    className={`flex flex-col items-center gap-1.5 p-4 rounded-xl border-2 transition-all duration-150 ${
                      role === r.key
                        ? 'border-primary bg-primary/5 text-primary' :'border-border text-muted-foreground hover:border-primary/40 hover:bg-muted/30'
                    }`}
                  >
                    <Icon size={22} />
                    <span className="text-sm font-semibold">{r.label}</span>
                    <span className="text-xs opacity-70">{r.desc}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ─── LOGIN FORM ─── */}
          {mode === 'login' && (
            <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-4 animate-fade-in">
              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    {...loginForm.register('email', { required: 'Email is required' })}
                    placeholder="you@example.com"
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                      loginForm.formState.errors.email ? 'border-destructive bg-destructive/5' : 'border-border bg-white'
                    }`}
                  />
                </div>
                {loginForm.formState.errors.email && (
                  <p className="text-xs text-destructive mt-1.5 font-medium">{loginForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-semibold text-foreground">Password</label>
                  <Link href="#" className="text-xs font-medium text-primary hover:underline">
                    Forgot password?
                  </Link>
                </div>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...loginForm.register('password', { required: 'Password is required' })}
                    placeholder="••••••••"
                    className={`w-full pl-10 pr-10 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all ${
                      loginForm.formState.errors.password ? 'border-destructive bg-destructive/5' : 'border-border bg-white'
                    }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {loginForm.formState.errors.password && (
                  <p className="text-xs text-destructive mt-1.5 font-medium">{loginForm.formState.errors.password.message}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="remember"
                  {...loginForm.register('remember')}
                  className="rounded accent-primary"
                />
                <label htmlFor="remember" className="text-sm text-muted-foreground">
                  Remember me for 30 days
                </label>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-[0.99] transition-all duration-150 shadow-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    Sign In
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              {/* Divider */}
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground font-medium">or continue with</span>
                <div className="flex-1 h-px bg-border" />
              </div>

              {/* Social Auth */}
              <button
                type="button"
                onClick={() => toast.info('Google OAuth — configure in Firebase/Auth0')}
                className="w-full py-3 rounded-xl border border-border bg-white text-sm font-semibold text-foreground hover:bg-muted transition-all duration-150 flex items-center justify-center gap-2.5"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </button>

              {/* Demo Credentials Box */}
              <div className="mt-4 rounded-xl border border-primary/20 bg-primary/4 overflow-hidden">
                <div className="px-4 py-2.5 bg-primary/8 border-b border-primary/15">
                  <p className="text-xs font-semibold text-primary">Demo Accounts — click to autofill</p>
                </div>
                <div className="divide-y divide-primary/10">
                  {demoCredentials.map((cred) => (
                    <div key={`demo-${cred.role}`} className="flex items-center justify-between px-4 py-3 gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-foreground">{cred.role}</p>
                        <p className="text-xs text-muted-foreground font-mono truncate">{cred.email}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => handleCopy(cred.email, `${cred.role} email`)}
                          className="p-1.5 rounded-lg hover:bg-primary/10 transition-colors"
                          title="Copy email"
                        >
                          {copiedField === `${cred.role} email` ? (
                            <CheckCircle size={12} className="text-primary" />
                          ) : (
                            <Copy size={12} className="text-muted-foreground" />
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => autofillCredentials(cred)}
                          className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors"
                        >
                          Use
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          )}

          {/* ─── SIGNUP FORM ─── */}
          {mode === 'signup' && (
            <form onSubmit={signupForm.handleSubmit(handleSignup)} className="space-y-4 animate-fade-in">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">First Name</label>
                  <input
                    type="text"
                    {...signupForm.register('firstName', { required: 'Required' })}
                    placeholder="Sophie"
                    className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      signupForm.formState.errors.firstName ? 'border-destructive bg-destructive/5' : 'border-border bg-white'
                    }`}
                  />
                  {signupForm.formState.errors.firstName && (
                    <p className="text-xs text-destructive mt-1">{signupForm.formState.errors.firstName.message}</p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-foreground mb-1.5">Last Name</label>
                  <input
                    type="text"
                    {...signupForm.register('lastName', { required: 'Required' })}
                    placeholder="Martin"
                    className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      signupForm.formState.errors.lastName ? 'border-destructive bg-destructive/5' : 'border-border bg-white'
                    }`}
                  />
                  {signupForm.formState.errors.lastName && (
                    <p className="text-xs text-destructive mt-1">{signupForm.formState.errors.lastName.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="email"
                    {...signupForm.register('email', { required: 'Email is required', pattern: { value: /^\S+@\S+\.\S+$/, message: 'Enter a valid email' } })}
                    placeholder="you@example.com"
                    className={`w-full pl-10 pr-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      signupForm.formState.errors.email ? 'border-destructive bg-destructive/5' : 'border-border bg-white'
                    }`}
                  />
                </div>
                {signupForm.formState.errors.email && (
                  <p className="text-xs text-destructive mt-1">{signupForm.formState.errors.email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Phone Number</label>
                <p className="text-xs text-muted-foreground mb-1.5">Used for booking confirmations via SMS</p>
                <div className="relative">
                  <Phone size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="tel"
                    {...signupForm.register('phone')}
                    placeholder="+33 6 12 34 56 78"
                    className="w-full pl-10 pr-4 py-3 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>

              {/* Hotel Manager Extra Fields */}
              {role === 'manager' && (
                <div className="space-y-3 p-4 rounded-xl bg-primary/4 border border-primary/15 animate-slide-up">
                  <p className="text-xs font-semibold text-primary uppercase tracking-wide">Hotel Details</p>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Hotel Name</label>
                    <input
                      type="text"
                      {...signupForm.register('hotelName', { required: role === 'manager' ? 'Hotel name is required' : false })}
                      placeholder="e.g. Seabel Alhambra Beach Golf & Spa"
                      className={`w-full px-4 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                        signupForm.formState.errors.hotelName ? 'border-destructive bg-destructive/5' : 'border-border bg-white'
                      }`}
                    />
                    {signupForm.formState.errors.hotelName && (
                      <p className="text-xs text-destructive mt-1">{signupForm.formState.errors.hotelName.message}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-foreground mb-1.5">Hotel City / Region</label>
                    <select
                      {...signupForm.register('hotelCity')}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="">Select city...</option>
                      {['Hammamet', 'Sousse', 'Port El Kantaoui', 'Tunis', 'Djerba', 'Monastir', 'Bizerte', 'Sfax'].map((c) => (
                        <option key={`city-opt-${c}`} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Password</label>
                <p className="text-xs text-muted-foreground mb-1.5">At least 8 characters with a number and special character</p>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    {...signupForm.register('password', {
                      required: 'Password is required',
                      minLength: { value: 8, message: 'Minimum 8 characters' },
                    })}
                    placeholder="••••••••"
                    className={`w-full pl-10 pr-10 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      signupForm.formState.errors.password ? 'border-destructive bg-destructive/5' : 'border-border bg-white'
                    }`}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupForm.formState.errors.password && (
                  <p className="text-xs text-destructive mt-1">{signupForm.formState.errors.password.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-foreground mb-1.5">Confirm Password</label>
                <div className="relative">
                  <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    {...signupForm.register('confirmPassword', { required: 'Please confirm your password' })}
                    placeholder="••••••••"
                    className={`w-full pl-10 pr-10 py-3 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      signupForm.formState.errors.confirmPassword ? 'border-destructive bg-destructive/5' : 'border-border bg-white'
                    }`}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                    {showConfirmPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                </div>
                {signupForm.formState.errors.confirmPassword && (
                  <p className="text-xs text-destructive mt-1">{signupForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>

              <div className="flex items-start gap-2">
                <input
                  type="checkbox"
                  id="terms"
                  {...signupForm.register('terms', { required: 'You must accept the terms' })}
                  className="rounded accent-primary mt-0.5"
                />
                <label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                  I agree to the{' '}
                  <Link href="#" className="text-primary hover:underline font-medium">Terms of Service</Link>
                  {' '}and{' '}
                  <Link href="#" className="text-primary hover:underline font-medium">Privacy Policy</Link>
                </label>
              </div>
              {signupForm.formState.errors.terms && (
                <p className="text-xs text-destructive -mt-2">{signupForm.formState.errors.terms.message}</p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 rounded-xl bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 active:scale-[0.99] transition-all duration-150 shadow-sm flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                    </svg>
                    Creating account...
                  </>
                ) : (
                  <>
                    Create {role === 'manager' ? 'Manager' : 'Player'} Account
                    <ArrowRight size={16} />
                  </>
                )}
              </button>

              {/* Social Auth */}
              <div className="flex items-center gap-3 my-2">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground font-medium">or</span>
                <div className="flex-1 h-px bg-border" />
              </div>
              <button
                type="button"
                onClick={() => toast.info('Google OAuth — configure in Firebase/Auth0')}
                className="w-full py-3 rounded-xl border border-border bg-white text-sm font-semibold text-foreground hover:bg-muted transition-all duration-150 flex items-center justify-center gap-2.5"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Sign up with Google
              </button>
            </form>
          )}

          {/* Switch Mode */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            {mode === 'login' ? "Don't have an account?" : 'Already have an account?'}{' '}
            <button
              onClick={() => setMode(mode === 'login' ? 'signup' : 'login')}
              className="text-primary font-semibold hover:underline"
            >
              {mode === 'login' ? 'Create one free' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}